import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { useLocationSuggestions, useSearchDestinations } from '@m/hooks/data/customer/map';
import { useFeatureGate } from '@m/hooks/data/featureFlags';
import {
  LocationSearchParams,
  LocationSuggestion,
  LocationSuggestionType,
} from '@m/types/api/LocationSuggestion';
import { CenterCoordinate } from '@m/types/MapTypes';
import {
  localISO8601String,
  parseDeepLinkDateTime,
  utcToVenueWallClock,
} from '@m/utils/dateTimeUtils';
import {
  trackDiscoveryLocationSearch,
  trackDiscoveryLocationSelect,
  trackDiscoverySearchBarInput,
} from 'apps/customer/utils/discoveryMapHeap';
import { trackMonthlyParkingLocationSearch } from 'apps/customer/utils/monthlyParkingHeap';

import {
  orderSearchDestinationsForDisplay,
  SearchDestination,
} from '../components/ReservationSearch';
import {
  DEFAULT_FOCUS_ZOOM,
  formatParkingStartingAtLabel,
  INITIAL_RADIUS_MILES,
  isSyntheticLocationLabel,
  PARKING_NEAR_MAP_AREA,
  PARKING_NEAR_ME,
} from '../utils';

import { DiscoveryQueryParams } from './useDiscoveryData';

export type SearchLoc = {
  address: string;
  eventEnd?: string;
  eventStart?: string;
  /**
   * When true, an event-scoped search still prefills the cushioned window but
   * leaves Starts/Ends editable (venue → upcoming events → pick event). Direct
   * event picks and marketing deep links omit this and keep dates locked.
   */
  flexibleEventDates?: boolean;
  latitude: number;
  longitude: number;
  /** Parent venue for an event selection — not sent as availability venueId. */
  relatedVenueId?: string;
  searchParams?: LocationSearchParams;
  suggestionType?: LocationSuggestionType;
  /** Venue name for event searches (shown in event results chrome). */
  venueName?: string;
};

/** Normalize API/wall-clock+Z event times so `parseDeepLinkDateTime` can re-read them. */
function normalizeEventWallClock(value?: string): string | undefined {
  if (!value) return undefined;
  const parsed = parseDeepLinkDateTime(value);
  // Prefer localISO8601String over toISOString() — fractional seconds break the parser.
  return parsed ? localISO8601String(parsed) : undefined;
}

/**
 * Event times with a `timeZone` are real UTC instants; convert them to the venue's
 * wall-clock (literal `Z`, same contract as the availability API). Without a
 * `timeZone` (or if it is invalid) fall back to the legacy wall-clock+Z handling.
 */
function eventTimeToVenueWallClock(value?: string, timeZone?: string): string | undefined {
  if (!value || !timeZone) return normalizeEventWallClock(value);
  return utcToVenueWallClock(value, timeZone) ?? normalizeEventWallClock(value);
}

const RECENT_SEARCHES_KEY = 'metropolis:reservationRecentSearches';
const MAX_RECENT_SEARCHES = 5;

function normalizeSuggestionType(type: LocationSuggestion['type']): LocationSuggestionType | null {
  if (typeof type === 'number') {
    return (
      (
        {
          1: 'zone',
          2: 'event',
          3: 'venue',
          4: 'poi',
          5: 'address',
        } as const
      )[type] ?? null
    );
  }

  const normalized = type.toLowerCase().replace('suggestion_type_', '');
  if (
    normalized === 'zone' ||
    normalized === 'event' ||
    normalized === 'venue' ||
    normalized === 'poi' ||
    normalized === 'address'
  ) {
    return normalized;
  }
  return null;
}

function getSuggestionCoordinates(
  suggestion: LocationSuggestion,
): { latitude: number; longitude: number } | null {
  const rawParams = suggestion.searchParams;
  const nestedCoordinates:
    | {
        lat?: number;
        latitude?: number;
        lng?: number;
        longitude?: number;
      }
    | undefined = rawParams?.poi?.coordinates ?? rawParams?.geoPoint ?? suggestion.coordinates;
  const latitude =
    nestedCoordinates?.latitude ?? nestedCoordinates?.lat ?? rawParams?.latitude ?? rawParams?.lat;
  const longitude =
    nestedCoordinates?.longitude ??
    nestedCoordinates?.lng ??
    rawParams?.longitude ??
    rawParams?.lng;

  return typeof latitude === 'number' && typeof longitude === 'number'
    ? { latitude, longitude }
    : null;
}

export function locationSuggestionToDestination(
  suggestion: LocationSuggestion,
  index: number,
): SearchDestination | null {
  const suggestionType = normalizeSuggestionType(suggestion.type);
  const coordinates = getSuggestionCoordinates(suggestion);
  if (!suggestionType || !coordinates) return null;

  const eventVenue = suggestion.searchParams?.eventVenue;
  const searchParams: LocationSearchParams = {
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  };
  switch (suggestionType) {
    case 'zone':
      searchParams.zoneId = suggestion.searchParams?.zoneId;
      break;
    case 'event':
      searchParams.eventId = suggestion.searchParams?.eventId ?? eventVenue?.eventId;
      searchParams.eventTimeZone = eventVenue?.timeZone;
      break;
    case 'venue':
      searchParams.venueId = suggestion.searchParams?.venueId ?? eventVenue?.venueId;
      break;
    case 'poi':
    case 'address':
      break;
    default: {
      const exhaustiveType: never = suggestionType;
      return exhaustiveType;
    }
  }

  const relatedVenueId =
    suggestionType === 'event'
      ? (eventVenue?.venueId ?? suggestion.searchParams?.venueId)
      : undefined;
  // Venue display name only — never fall back to a street address (that leaked into
  // the upcoming-events modal title as "near 335-337 Newark Ave…").
  let venueName: string | undefined;
  if (suggestionType === 'venue') {
    venueName = suggestion.name;
  } else if (suggestionType === 'event') {
    venueName = eventVenue?.venueName;
  }

  return {
    address: suggestion.address,
    center: [coordinates.longitude, coordinates.latitude],
    eventEnd: eventTimeToVenueWallClock(eventVenue?.eventEnd, eventVenue?.timeZone),
    eventStart: eventTimeToVenueWallClock(eventVenue?.eventStart, eventVenue?.timeZone),
    id:
      suggestion.id ??
      [
        suggestionType,
        searchParams.zoneId ?? searchParams.eventId ?? searchParams.venueId ?? index,
        suggestion.name,
      ].join(':'),
    parkingLabel: formatParkingStartingAtLabel(eventVenue?.rateStartsAt),
    place_name: suggestion.address ? `${suggestion.name}, ${suggestion.address}` : suggestion.name,
    relatedVenueId,
    searchParams,
    suggestionType,
    text: suggestion.name,
    venueName,
  };
}

function isSearchDestination(entry: unknown): entry is SearchDestination {
  if (!entry || typeof entry !== 'object') return false;
  const dest = entry as Partial<SearchDestination>;
  return (
    typeof dest.id === 'string' &&
    typeof dest.text === 'string' &&
    typeof dest.place_name === 'string' &&
    Array.isArray(dest.center)
  );
}

function readRecentSearches(): SearchDestination[] {
  try {
    const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isSearchDestination) : [];
  } catch {
    return [];
  }
}

function writeRecentSearches(searches: SearchDestination[]): void {
  try {
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch {
    // Storage unavailable (private mode / quota) — recent searches just won't persist.
  }
}

export type DiscoverySearchOptions = {
  /**
   * When true (default), auto-fill "Parking near me" / map-area while idle.
   * Home passes false so the field starts empty until the user picks a place
   * or taps "Use my current location".
   */
  autoFillSyntheticLocation?: boolean;
  /** When true, a committed location search is also a monthly-parking funnel step. */
  monthly?: boolean;
};

export type DiscoverySearchResult = {
  /**
   * Sets the field to "Parking near me" and commits the given coordinates.
   * Home should call this with explicit coords so the label always populates.
   */
  commitParkingNearMe: (coords: { latitude: number; longitude: number }) => void;
  destinations: SearchDestination[];
  handleClearSearch: () => void;
  /**
   * Full reset: clears field text, committed pin, and geocoder draft.
   * Used by Home so Clear empties the whole search form (not just the draft).
   */
  handleResetSearch: () => void;
  /** Commits the first geocoder hit. Returns false when debounce is pending or there are no results. */
  handleSearch: () => boolean;
  handleSearchLocation: (dest: SearchDestination) => void;
  onUseCurrentLocation: (() => void) | undefined;
  /** Previously selected destinations, most-recent first (persisted across visits). */
  recentSearches: SearchDestination[];
  /**
   * Restores field text after an abandoned draft edit. When the text matches the
   * last committed location query, also restores `selectedSearchLoc`.
   */
  restoreSearchQuery: (q: string) => void;
  searchQuery: string;
  searchResetKey: string | undefined;
  searchResultsCurrent: boolean;
  selectedSearchLoc: SearchLoc | null;
  setSearchQuery: (q: string) => void;
  showSearchEmptyState: boolean;
};

export function useDiscoverySearch(
  mapCenter: [number, number] | undefined,
  userLocation: { latitude: number; longitude: number } | null,
  setCenterCoordinate: (center: CenterCoordinate) => void,
  setQueryParams: (params: DiscoveryQueryParams | null) => void,
  initialSearchLoc?: {
    eventEnd?: string;
    eventId?: string;
    eventStart?: string;
    eventTimeZone?: string;
    flexibleEventDates?: boolean;
    latitude: number;
    longitude: number;
    venueId?: string;
    /** Display name for locked Event details / venue dialog (not availability). */
    venueName?: string;
    zoneId?: string;
  } | null,
  /** Address label from URL (`?location=`) when navigating from /home. */
  initialLocationLabel?: string,
  options?: DiscoverySearchOptions,
): DiscoverySearchResult {
  const autoFillSyntheticLocation = options?.autoFillSyntheticLocation ?? true;
  const monthlySearch = options?.monthly === true;
  const [searchQuery, setSearchQueryState] = useState(
    initialLocationLabel ?? (initialSearchLoc ? PARKING_NEAR_ME : ''),
  );
  const [debouncedQuery, setDebouncedQuery] = useState('');
  let initialSuggestionType: LocationSuggestionType | undefined;
  if (initialSearchLoc?.eventId) initialSuggestionType = 'event';
  else if (initialSearchLoc?.venueId) initialSuggestionType = 'venue';
  else if (initialSearchLoc?.zoneId) initialSuggestionType = 'zone';
  const initialLoc: SearchLoc | null = initialSearchLoc
    ? {
        // Unlabeled lat/lng is the Home near-me handoff — never a coordinate string.
        address: initialLocationLabel ?? PARKING_NEAR_ME,
        eventEnd: initialSearchLoc.eventEnd,
        eventStart: initialSearchLoc.eventStart,
        flexibleEventDates: initialSearchLoc.flexibleEventDates,
        latitude: initialSearchLoc.latitude,
        longitude: initialSearchLoc.longitude,
        // Parent venue on event deep links is display/dialog only — not availability.
        relatedVenueId: initialSearchLoc.eventId ? initialSearchLoc.venueId : undefined,
        searchParams: {
          eventId: initialSearchLoc.eventId,
          eventTimeZone: initialSearchLoc.eventTimeZone,
          latitude: initialSearchLoc.latitude,
          longitude: initialSearchLoc.longitude,
          venueId: initialSearchLoc.eventId ? undefined : initialSearchLoc.venueId,
          zoneId: initialSearchLoc.zoneId,
        },
        suggestionType: initialSuggestionType,
        venueName: initialSearchLoc.venueName,
      }
    : null;
  const [selectedSearchLoc, setSelectedSearchLoc] = useState<SearchLoc | null>(() => initialLoc);
  /** Last committed field text + pin — used to restore both after draft dismiss. */
  const committedSearchRef = useRef<{ loc: SearchLoc; query: string } | null>(
    initialLoc
      ? {
          query: initialLocationLabel ?? initialLoc.address,
          loc: initialLoc,
        }
      : null,
  );
  const searchSelectionRef = useRef(false);
  /** Query text already reported to Heap — set on settle, or preemptively on selection. */
  const lastTrackedSearchQueryRef = useRef<string | null>(null);
  /** After the user clears/focuses away a synthetic label, don't auto-reinsert it. */
  const suppressSyntheticLabelRef = useRef(false);

  const rememberCommitted = useCallback((query: string, loc: SearchLoc) => {
    committedSearchRef.current = { query, loc };
    setSelectedSearchLoc(loc);
  }, []);

  const [recentSearches, setRecentSearches] = useState<SearchDestination[]>(readRecentSearches);

  // Only actual typed/picked places — not synthetic "Parking near me" / map-area labels.
  // Stores the full destination (not just its label) so selecting a recent search
  // commits it immediately instead of re-running the geocoder.
  const addRecentSearch = useCallback((dest: SearchDestination) => {
    if (!dest.text || isSyntheticLocationLabel(dest.text)) return;
    setRecentSearches((prev) => {
      const next = [dest, ...prev.filter((entry) => entry.text !== dest.text)].slice(
        0,
        MAX_RECENT_SEARCHES,
      );
      writeRecentSearches(next);
      return next;
    });
  }, []);

  const { data: freeTextSearchGate } = useFeatureGate('growth_free-text-search-enabled');
  const freeTextSearchEnabled = freeTextSearchGate?.enabled ?? false;

  const locationSuggestions = useLocationSuggestions(
    debouncedQuery,
    userLocation,
    freeTextSearchEnabled,
  );
  const backendDestinations = useMemo(
    () =>
      (locationSuggestions.data?.suggestions ?? [])
        .map(locationSuggestionToDestination)
        .filter((destination): destination is SearchDestination => destination !== null),
    [locationSuggestions.data?.suggestions],
  );
  // Mapbox always runs alongside the backend so Places (up to 5) fill in even
  // when the backend only returns venues, events or parking lots.
  const {
    data: searchData,
    isError: isSearchError,
    isFetching: isSearchFetching,
    isSuccess: isSearchSuccess,
  } = useSearchDestinations({
    query: debouncedQuery,
    proximity: mapCenter,
    dedupeNearDuplicates: true,
  });
  const destinations = useMemo<SearchDestination[]>(() => {
    if (!freeTextSearchEnabled) return searchData?.features ?? [];
    const backendLabels = new Set(
      backendDestinations.map((destination) => destination.text.toLowerCase()),
    );
    const mapboxPlaces = (searchData?.features ?? [])
      .filter((destination) => !backendLabels.has(destination.text.toLowerCase()))
      .map((destination) => ({
        ...destination,
        address: destination.place_name,
        searchParams: {
          latitude: destination.center[1],
          longitude: destination.center[0],
        },
        suggestionType: 'address' as const,
      }));
    return orderSearchDestinationsForDisplay([...backendDestinations, ...mapboxPlaces]);
  }, [backendDestinations, freeTextSearchEnabled, searchData?.features]);
  let suggestionsSettled = isSearchSuccess && !isSearchFetching;
  if (freeTextSearchEnabled) {
    // Settled once both sources finished (success or error) so an empty list
    // can show "No locations found".
    suggestionsSettled =
      !locationSuggestions.isFetching &&
      !isSearchFetching &&
      (locationSuggestions.isFetched || isSearchSuccess || isSearchError);
  }
  const showSearchEmptyState =
    freeTextSearchEnabled &&
    debouncedQuery.length >= 3 &&
    debouncedQuery === searchQuery &&
    suggestionsSettled &&
    destinations.length === 0;

  const debouncedTrackSearchInput = useMemo(
    () =>
      debounce((query: string) => {
        trackDiscoverySearchBarInput({ action: 'typing', searchQuery: query });
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedTrackSearchInput.cancel();
    };
  }, [debouncedTrackSearchInput]);

  // Only report once the current debounced query has successfully settled —
  // firing earlier records 0/stale counts from the previous key. Dedupe per query
  // text; selection handlers preemptively claim their text so a post-selection
  // refetch doesn't re-fire it.
  useEffect(() => {
    if (debouncedQuery.length < 3 || debouncedQuery !== searchQuery) return;
    if (!suggestionsSettled) return;
    if (lastTrackedSearchQueryRef.current === debouncedQuery) return;
    lastTrackedSearchQueryRef.current = debouncedQuery;
    trackDiscoveryLocationSearch({
      searchQuery: debouncedQuery,
      resultsCount: destinations.length,
    });
  }, [debouncedQuery, destinations.length, searchQuery, suggestionsSettled]);

  // Debounce search — skip re-opening results after a destination selection.
  // Synthetic near-me / map-area labels must not hit the geocoder.
  useEffect(() => {
    if (searchSelectionRef.current) {
      searchSelectionRef.current = false;
      return undefined;
    }
    if (isSyntheticLocationLabel(searchQuery)) {
      setDebouncedQuery('');
      return undefined;
    }
    const t = setTimeout(
      () => {
        if (searchQuery.length < 3) {
          setDebouncedQuery('');
        } else {
          setDebouncedQuery(searchQuery);
        }
      },
      searchQuery.length < 3 ? 0 : 300,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Draft typing / Clear (X) only update the field text. The committed pin stays
  // until the user picks a destination from the list (or Enter / near-me). That
  // keeps date pickers enabled and avoids wiping dates mid-edit; dismiss restores
  // the previous field label via `restoreSearchQuery`.
  const handleClearSearch = useCallback(() => {
    suppressSyntheticLabelRef.current = true;
    debouncedTrackSearchInput.cancel();
    trackDiscoverySearchBarInput({ action: 'clear', searchQuery: '' });
    setSearchQueryState('');
    setDebouncedQuery('');
  }, [debouncedTrackSearchInput]);

  // Home Clear: wipe committed pin too so dates disable and Find parking errors.
  const handleResetSearch = useCallback(() => {
    suppressSyntheticLabelRef.current = true;
    debouncedTrackSearchInput.cancel();
    trackDiscoverySearchBarInput({ action: 'clear', searchQuery: '' });
    setSearchQueryState('');
    setDebouncedQuery('');
    setSelectedSearchLoc(null);
    committedSearchRef.current = null;
  }, [debouncedTrackSearchInput]);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      // Any edit away from the exact synthetic label must stick (including partial
      // edits of "Parking near me") so the geo auto-label effect cannot overwrite.
      if (!isSyntheticLocationLabel(query)) {
        suppressSyntheticLabelRef.current = true;
      }
      if (!query) {
        debouncedTrackSearchInput.cancel();
        trackDiscoverySearchBarInput({ action: 'clear', searchQuery: '' });
      } else if (!isSyntheticLocationLabel(query)) {
        debouncedTrackSearchInput(query);
      }
      setSearchQueryState(query);
    },
    [debouncedTrackSearchInput],
  );

  const restoreSearchQuery = useCallback((query: string) => {
    if (!isSyntheticLocationLabel(query)) {
      suppressSyntheticLabelRef.current = true;
    } else {
      suppressSyntheticLabelRef.current = false;
    }
    setSearchQueryState(query);
    const committed = committedSearchRef.current;
    if (committed && committed.query === query) {
      setSelectedSearchLoc(committed.loc);
    }
  }, []);

  const handleSearch = useCallback((): boolean => {
    // Only run search when the displayed query matches the query used for results
    if (searchQuery !== debouncedQuery || destinations.length === 0) {
      return false;
    }

    const first = destinations[0];
    const [lng, lat] = first.center;
    suppressSyntheticLabelRef.current = false;
    searchSelectionRef.current = true;
    setSearchQueryState(first.text);
    setDebouncedQuery('');
    rememberCommitted(first.text, {
      address: first.place_name,
      eventEnd: first.eventEnd,
      eventStart: first.eventStart,
      latitude: lat,
      longitude: lng,
      relatedVenueId: first.relatedVenueId,
      searchParams: first.searchParams,
      suggestionType: first.suggestionType,
      venueName: first.venueName ?? (first.suggestionType === 'venue' ? first.text : undefined),
    });
    addRecentSearch(first);
    setCenterCoordinate({ longitude: lng, latitude: lat, zoom: DEFAULT_FOCUS_ZOOM });
    setQueryParams({
      latitude: lat,
      longitude: lng,
      maxDistanceMiles: INITIAL_RADIUS_MILES,
      ...first.searchParams,
    });
    lastTrackedSearchQueryRef.current = first.text;
    trackDiscoveryLocationSelect({
      address: first.place_name,
      latitude: lat,
      longitude: lng,
    });
    if (monthlySearch) {
      trackMonthlyParkingLocationSearch({
        address: first.place_name,
        latitude: lat,
        longitude: lng,
        surface: 'map',
      });
    }
    return true;
  }, [
    addRecentSearch,
    debouncedQuery,
    destinations,
    monthlySearch,
    rememberCommitted,
    searchQuery,
    setCenterCoordinate,
    setQueryParams,
  ]);
  const handleSearchLocation = useCallback(
    (dest: SearchDestination) => {
      const [lng, lat] = dest.center;
      suppressSyntheticLabelRef.current = false;
      searchSelectionRef.current = true;
      setSearchQueryState(dest.text);
      // Keep the field label, but do not re-query suggestions for the exact
      // selected name — that response is often venue-only and would wipe the
      // event rows needed by the venue events dialog.
      setDebouncedQuery('');
      rememberCommitted(dest.text, {
        address: dest.place_name,
        eventEnd: dest.eventEnd,
        eventStart: dest.eventStart,
        flexibleEventDates: dest.flexibleEventDates,
        latitude: lat,
        longitude: lng,
        relatedVenueId: dest.relatedVenueId,
        searchParams: dest.searchParams,
        suggestionType: dest.suggestionType,
        venueName: dest.venueName ?? (dest.suggestionType === 'venue' ? dest.text : undefined),
      });
      addRecentSearch(dest);
      setCenterCoordinate({ longitude: lng, latitude: lat, zoom: DEFAULT_FOCUS_ZOOM });
      setQueryParams({
        latitude: lat,
        longitude: lng,
        maxDistanceMiles: INITIAL_RADIUS_MILES,
        ...dest.searchParams,
      });
      lastTrackedSearchQueryRef.current = dest.text;
      trackDiscoveryLocationSelect({
        address: dest.place_name,
        latitude: lat,
        longitude: lng,
      });
      if (monthlySearch) {
        trackMonthlyParkingLocationSearch({
          address: dest.place_name,
          latitude: lat,
          longitude: lng,
          surface: 'map',
        });
      }
    },
    [addRecentSearch, monthlySearch, rememberCommitted, setCenterCoordinate, setQueryParams],
  );

  const commitParkingNearMe = useCallback(
    (coords: { latitude: number; longitude: number }) => {
      const address = PARKING_NEAR_ME;
      suppressSyntheticLabelRef.current = false;
      searchSelectionRef.current = true;
      setSearchQueryState(address);
      setDebouncedQuery('');
      rememberCommitted(address, {
        address,
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      setCenterCoordinate({
        latitude: coords.latitude,
        longitude: coords.longitude,
        zoom: DEFAULT_FOCUS_ZOOM,
      });
      setQueryParams({
        latitude: coords.latitude,
        longitude: coords.longitude,
        maxDistanceMiles: INITIAL_RADIUS_MILES,
      });
      lastTrackedSearchQueryRef.current = address;
      trackDiscoveryLocationSelect({
        address,
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      if (monthlySearch) {
        trackMonthlyParkingLocationSearch({
          address,
          latitude: coords.latitude,
          longitude: coords.longitude,
          surface: 'map',
        });
      }
    },
    [monthlySearch, rememberCommitted, setCenterCoordinate, setQueryParams],
  );

  // Park Now: auto-fill AND commit a synthetic pin so date pickers enable on a
  // direct visit. Prefer geo ("Parking near me"); else map center ("near map area").
  // Home disables this so "Where to?" stays empty until an explicit commit.
  // Once a synthetic pin exists, keep it — do not re-pin from mapCenter on pan
  // (that churns searchResetKey) or overwrite "Parking near me" from Home when
  // browser geo is unavailable.
  useEffect(() => {
    if (!autoFillSyntheticLocation) return;
    if (suppressSyntheticLabelRef.current) return;
    if (selectedSearchLoc && !isSyntheticLocationLabel(selectedSearchLoc.address)) {
      return;
    }
    if (searchQuery && !isSyntheticLocationLabel(searchQuery)) {
      return;
    }

    if (userLocation) {
      const alreadyNearMe =
        selectedSearchLoc?.address === PARKING_NEAR_ME &&
        selectedSearchLoc.latitude === userLocation.latitude &&
        selectedSearchLoc.longitude === userLocation.longitude;
      if (!alreadyNearMe) {
        commitParkingNearMe(userLocation);
      }
      // Do not rewrite the field text when the pin is already near-me — that
      // fights Clear (X) and in-place edits of "Parking near me".
      return;
    }

    // Preserve hydrated / prior synthetic commits (near-me from Home, or the
    // one-shot map-area pin). Map pans update mapCenter and must not re-commit.
    // Don't overwrite an empty or in-progress field — Clear / typing owns that.
    if (selectedSearchLoc && isSyntheticLocationLabel(selectedSearchLoc.address)) {
      return;
    }

    if (!mapCenter) return;
    const [longitude, latitude] = mapCenter;
    const address = PARKING_NEAR_MAP_AREA;
    searchSelectionRef.current = true;
    suppressSyntheticLabelRef.current = false;
    setSearchQueryState(address);
    setDebouncedQuery('');
    rememberCommitted(address, { address, latitude, longitude });
    lastTrackedSearchQueryRef.current = address;
  }, [
    autoFillSyntheticLocation,
    userLocation,
    mapCenter,
    selectedSearchLoc,
    searchQuery,
    commitParkingNearMe,
    rememberCommitted,
  ]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!userLocation) return;
    commitParkingNearMe(userLocation);
  }, [commitParkingNearMe, userLocation]);

  const searchResetKey = selectedSearchLoc
    ? [
        selectedSearchLoc.latitude.toFixed(4),
        selectedSearchLoc.longitude.toFixed(4),
        selectedSearchLoc.searchParams?.zoneId,
        selectedSearchLoc.searchParams?.eventId,
        selectedSearchLoc.searchParams?.venueId,
      ].join(':')
    : undefined;

  return {
    searchQuery,
    setSearchQuery: handleSearchQueryChange,
    restoreSearchQuery,
    destinations,
    selectedSearchLoc,
    searchResetKey,
    searchResultsCurrent: isSyntheticLocationLabel(searchQuery) || searchQuery === debouncedQuery,
    showSearchEmptyState,
    handleSearch,
    handleClearSearch,
    handleResetSearch,
    handleSearchLocation,
    commitParkingNearMe,
    onUseCurrentLocation: userLocation ? handleUseCurrentLocation : undefined,
    recentSearches,
  };
}
